import React from 'react';
import PropTypes from 'prop-types';
import styles from './select.module.css';

/**
 * Accessible styled select wrapper.
 *
 * @param {Object}   props
 * @param {{label:string,value:string}[]} props.options  Array of option objects to render.
 * @param {string}   [props.value]        Current selected value (controlled). Empty string / undefined shows placeholder.
 * @param {(value:string, event:React.ChangeEvent<HTMLSelectElement>)=>void} [props.onChange]
 *                                          Fired when selection changes; first arg is the new value.
 * @param {string}   [props.placeholder='Select...'] Placeholder text shown as a hidden disabled option until a value is chosen.
 * @param {string}   [props.name]         Name attribute for the native select.
 * @param {boolean}  [props.disabled]     Disable interaction.
 * @param {string}   [props.className]    Additional wrapper class names.
 * @param {string}   [props.id]           Id for the select (auto-generated if omitted).
 */
const Select = ({ options = [], value, onChange, placeholder = 'Select...', name, disabled, className = '', id }) => {
  // Normalize: ensure we only use objects with label & value strings
  const normalized = Array.isArray(options)
    ? options.filter((o) => o && typeof o.label === 'string' && typeof o.value === 'string')
    : [];

  const selectId = id || `select-${name || Math.random().toString(36).slice(2)}`;

  return (
    <div className={`${styles.wrapper} ${disabled ? styles.disabled : ''} ${className}`}>
      <select
        id={selectId}
        name={name}
        value={value ?? ''}
        onChange={(e) => onChange && onChange(e.target.value, e)}
        disabled={disabled}
        className={styles.select}
      >
        {placeholder && (value === undefined || value === '' || value === null) && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {normalized.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
};

Select.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string
};

export default Select;
